import React, { useState } from 'react';
import MainLayout from '../../components/layout/MainLayout';
import HondaLoader from '../../components/common/HondaLoader';
import { sanitizeInput } from '../../utils/inputValidation';
import { getPrendasTracking } from '../../services/prendasService';
import { FaHandHoldingUsd } from "react-icons/fa";

const TrackingPrendas = ({ user, onLogout, onNavigate }) => {
  const [group, setGroup] = useState('');
  const [order, setOrder] = useState('');
  const [buscando, setBuscando] = useState(false);
  const [casoEncontrado, setCasoEncontrado] = useState(null);
  const [trackingEventos, setTrackingEventos] = useState([]);
  const [error, setError] = useState(null);

  // ============================================================
  // DETERMINAR CATEGORÍA DEL EVENTO
  // ============================================================
  const determinarCategoria = (eventDescription) => {
    const desc = String(eventDescription || '').toUpperCase();

    if (
      desc.includes('APROBADO') ||
      desc.includes('COMPLETADO') ||
      desc.includes('APTO') ||
      desc.includes('FINALIZADO') ||
      desc.includes('ÉXITO') ||
      desc.includes('EXITO')
    ) {
      return 'success';
    }

    if (
      desc.includes('RECHAZADO') ||
      desc.includes('ERROR') ||
      desc.includes('NO APTO') ||
      desc.includes('FALLIDO')
    ) {
      return 'error';
    }

    if (
      desc.includes('PENDIENTE') ||
      desc.includes('REVISION') ||
      desc.includes('REVISIÓN') ||
      desc.includes('INCOMPLETO') ||
      desc.includes('EN REVISION') ||
      desc.includes('EN REVISIÓN')
    ) {
      return 'warning';
    }

    return 'info';
  };

  // ============================================================
  // FORMATEAR FECHA
  // ============================================================
  const formatearFecha = (fechaStr) => {
    if (!fechaStr) {
      return 'N/A';
    }

    try {
      const fechaTexto = String(fechaStr);

      const fechaNormalizada = fechaTexto.replace('T', ' ');

      const partesFechaHora = fechaNormalizada.split(' ');

      const fecha = partesFechaHora[0];
      const hora = partesFechaHora[1];

      if (!fecha) {
        return fechaTexto;
      }

      const partes = fecha.split('-');

      if (
        partes.length === 3 &&
        partes[0].length === 4
      ) {
        const [year, month, day] = partes;

        return `${day}/${month}/${year}${hora ? ` ${hora}` : ''
          }`;
      }

      return fechaTexto;
    } catch (err) {
      console.error(
        '[TrackingPrendas] Error formateando fecha:',
        err
      );

      return fechaStr;
    }
  };

  // ============================================================
  // OBTENER FECHA DEL EVENTO
  // ============================================================
  const obtenerFechaEvento = (evento) => {
    return (
      evento?.eventDate ||
      evento?.eventDateTime ||
      evento?.date ||
      evento?.createdAt ||
      evento?.recordDate ||
      ''
    );
  };

  // ============================================================
  // OBTENER DESCRIPCIÓN DEL EVENTO
  // ============================================================
  const obtenerDescripcionEvento = (evento) => {
    return (
      evento?.eventDescription ||
      evento?.description ||
      evento?.event ||
      evento?.status ||
      evento?.estado ||
      'Evento sin descripción'
    );
  };

  // ============================================================
  // OBTENER USUARIO DEL EVENTO
  // ============================================================
  const obtenerUsuarioEvento = (evento) => {
    return (
      evento?.userName ||
      evento?.username ||
      evento?.user ||
      evento?.userFullName ||
      evento?.createdBy ||
      'N/A'
    );
  };

  // ============================================================
  // OBTENER DETALLE / NOTAS
  // ============================================================
  const obtenerDetalleEvento = (evento) => {
    return (
      evento?.notes ||
      evento?.note ||
      evento?.detail ||
      evento?.details ||
      evento?.observations ||
      evento?.observation ||
      'Sin notas adicionales'
    );
  };

  // ============================================================
  // CALCULAR TIEMPO TOTAL
  // ============================================================
  const calcularTiempoTotal = (eventos) => {
    if (!eventos || eventos.length < 2) {
      return 'N/A';
    }

    try {
      const fechas = eventos
        .map((evento) => obtenerFechaEvento(evento))
        .filter(Boolean)
        .map((fecha) => new Date(fecha))
        .filter(
          (fecha) => !Number.isNaN(fecha.getTime())
        );

      if (fechas.length < 2) {
        return 'N/A';
      }

      const fechaMasAntigua = new Date(
        Math.min(
          ...fechas.map((fecha) => fecha.getTime())
        )
      );

      const fechaMasReciente = new Date(
        Math.max(
          ...fechas.map((fecha) => fecha.getTime())
        )
      );

      const diffMs =
        fechaMasReciente.getTime() -
        fechaMasAntigua.getTime();

      if (diffMs < 0) {
        return 'N/A';
      }

      const dias = Math.floor(
        diffMs / (1000 * 60 * 60 * 24)
      );

      const horas = Math.floor(
        (diffMs % (1000 * 60 * 60 * 24)) /
        (1000 * 60 * 60)
      );

      const minutos = Math.floor(
        (diffMs % (1000 * 60 * 60)) /
        (1000 * 60)
      );

      if (dias > 0) {
        return `${dias} día${dias > 1 ? 's' : ''
          } ${horas}h ${minutos}min`;
      }

      if (horas > 0) {
        return `${horas}h ${minutos}min`;
      }

      return `${minutos} min`;
    } catch (err) {
      console.error(
        '[TrackingPrendas] Error calculando tiempo total:',
        err
      );

      return 'N/A';
    }
  };

  // ============================================================
  // NORMALIZAR RESPUESTA DEL BACKEND
  // ============================================================
  const normalizarEventos = (response) => {
    if (Array.isArray(response)) {
      return response;
    }

    if (Array.isArray(response?.data)) {
      return response.data;
    }

    if (Array.isArray(response?.events)) {
      return response.events;
    }

    if (Array.isArray(response?.tracking)) {
      return response.tracking;
    }

    if (Array.isArray(response?.trackingEvents)) {
      return response.trackingEvents;
    }

    if (Array.isArray(response?.data?.events)) {
      return response.data.events;
    }

    if (Array.isArray(response?.data?.tracking)) {
      return response.data.tracking;
    }

    if (
      Array.isArray(
        response?.data?.trackingEvents
      )
    ) {
      return response.data.trackingEvents;
    }

    return [];
  };

  // ============================================================
  // BUSCAR TRACKING
  // ============================================================
  const handleBuscar = async () => {
    const groupValue = group.trim();
    const orderValue = order.trim();

    // ----------------------------------------------------------
    // VALIDACIÓN
    // ----------------------------------------------------------
    if (!groupValue || !orderValue) {
      setError(
        'Por favor, complete el grupo y el orden.'
      );

      setCasoEncontrado(null);
      setTrackingEventos([]);

      return;
    }

    setBuscando(true);
    setError(null);
    setCasoEncontrado(null);
    setTrackingEventos([]);

    try {
      console.log(
        '[TrackingPrendas] Parámetros ingresados:',
        {
          group: groupValue,
          order: orderValue,
        }
      );

      // --------------------------------------------------------
      // LLAMAR AL ENDPOINT
      // El servicio se encarga del formato de:
      // group -> 6 dígitos
      // order -> 4 dígitos
      // --------------------------------------------------------
      const response = await getPrendasTracking(
        groupValue,
        orderValue,
        user.ce_ID ? user.ce_ID : 0
      );

      console.log(
        '[TrackingPrendas] Respuesta del endpoint:',
        response
      );

      // --------------------------------------------------------
      // NORMALIZAR EVENTOS
      // --------------------------------------------------------
      const eventos = normalizarEventos(response);

      console.log(
        '[TrackingPrendas] Eventos normalizados:',
        eventos
      );

      // --------------------------------------------------------
      // VALIDAR RESULTADO
      // --------------------------------------------------------
      if (!eventos.length) {
        setError(
          'No se encontraron eventos para el grupo y orden especificados.'
        );

        setCasoEncontrado(null);
        setTrackingEventos([]);

        return;
      }

      // --------------------------------------------------------
      // ORDENAR EVENTOS
      // MÁS RECIENTE -> MÁS ANTIGUO
      // --------------------------------------------------------
      const eventosOrdenados = [...eventos].sort(
        (a, b) => {
          const fechaA = new Date(
            obtenerFechaEvento(a)
          ).getTime();

          const fechaB = new Date(
            obtenerFechaEvento(b)
          ).getTime();

          if (
            Number.isNaN(fechaA) ||
            Number.isNaN(fechaB)
          ) {
            return 0;
          }

          return fechaB - fechaA;
        }
      );

      // --------------------------------------------------------
      // MAPEAR EVENTOS PARA LA UI
      // --------------------------------------------------------
      const eventosMapeados =
        eventosOrdenados.map(
          (evento, index) => {
            const descripcion =
              obtenerDescripcionEvento(
                evento
              );

            const fecha =
              obtenerFechaEvento(
                evento
              );

            const usuario =
              obtenerUsuarioEvento(
                evento
              );

            const detalle =
              obtenerDetalleEvento(
                evento
              );

            return {
              id:
                evento?.id ||
                evento?.eventId ||
                evento?.trackingId ||
                index + 1,

              evento: descripcion,

              fecha: formatearFecha(
                fecha
              ),

              usuario,

              detalle,

              categoria:
                determinarCategoria(
                  descripcion
                ),
            };
          }
        );

      // --------------------------------------------------------
      // OBTENER ÚLTIMO EVENTO
      // --------------------------------------------------------
      const ultimoEvento =
        eventosOrdenados[0];

      const ultimoEventoDescripcion =
        obtenerDescripcionEvento(
          ultimoEvento
        );

      const ultimaFecha =
        obtenerFechaEvento(
          ultimoEvento
        );

      // --------------------------------------------------------
      // RESUMEN DEL CASO
      // --------------------------------------------------------
      setCasoEncontrado({
        grupo:
          ultimoEvento?.group ||
          ultimoEvento?.grupo ||
          ultimoEvento?.groupNumber ||
          eventos[0]?.group ||
          eventos[0]?.grupo ||
          eventos[0]?.groupNumber ||
          groupValue,

        orden:
          ultimoEvento?.order ||
          ultimoEvento?.orden ||
          ultimoEvento?.orderNumber ||
          eventos[0]?.order ||
          eventos[0]?.orden ||
          eventos[0]?.orderNumber ||
          orderValue,

        estado:
          ultimoEventoDescripcion,

        totalEventos:
          eventos.length,

        ultimaActualizacion:
          formatearFecha(
            ultimaFecha
          ),

        tiempoTotal:
          calcularTiempoTotal(
            eventosOrdenados
          ),
      });

      // --------------------------------------------------------
      // GUARDAR EVENTOS
      // --------------------------------------------------------
      setTrackingEventos(
        eventosMapeados
      );
    } catch (err) {
      console.error(
        '[TrackingPrendas] Error al buscar tracking:',
        err
      );

      setError(
        err?.message ||
        'Error al obtener el tracking de eventos.'
      );

      setCasoEncontrado(null);
      setTrackingEventos([]);
    } finally {
      setBuscando(false);
    }
  };

  // ============================================================
  // COLOR DE CATEGORÍA
  // ============================================================
  const getCategoriaColor = (categoria) => {
    switch (categoria) {
      case 'success':
        return 'bg-green-100 text-green-700';

      case 'warning':
        return 'bg-yellow-100 text-yellow-700';

      case 'error':
        return 'bg-red-100 text-red-700';

      default:
        return 'bg-blue-100 text-blue-700';
    }
  };

  // ============================================================
  // COLOR DEL ICONO DE CATEGORÍA
  // ============================================================
  const getCategoriaIconoColor = (categoria) => {
    switch (categoria) {
      case 'success':
        return 'bg-green-500 border-green-600';

      case 'warning':
        return 'bg-yellow-500 border-yellow-600';

      case 'error':
        return 'bg-red-500 border-red-600';

      default:
        return 'bg-blue-500 border-blue-600';
    }
  };

  // ============================================================
  // ICONO DE CATEGORÍA
  // ============================================================
  const getCategoriaIcono = (categoria) => {
    switch (categoria) {
      case 'success':
        return 'check_circle';

      case 'warning':
        return 'warning';

      case 'error':
        return 'error';

      default:
        return 'info';
    }
  };

  // ============================================================
  // TEXTO DE CATEGORÍA
  // ============================================================
  const getCategoriaTexto = (categoria) => {
    switch (categoria) {
      case 'success':
        return 'Completado';

      case 'warning':
        return 'Pendiente';

      case 'error':
        return 'Error';

      default:
        return 'Info';
    }
  };

  // ============================================================
  // COLOR DEL ESTADO
  // ============================================================
  const getEstadoColor = (estado) => {
    const estadoNormalizado = String(
      estado || ''
    ).toUpperCase();

    if (
      estadoNormalizado.includes('APROBADO') ||
      estadoNormalizado.includes('COMPLETADO') ||
      estadoNormalizado.includes('APTO') ||
      estadoNormalizado.includes('FINALIZADO')
    ) {
      return 'text-green-600 bg-green-100';
    }

    if (
      estadoNormalizado.includes('RECHAZADO') ||
      estadoNormalizado.includes('ERROR') ||
      estadoNormalizado.includes('NO APTO') ||
      estadoNormalizado.includes('FALLIDO')
    ) {
      return 'text-red-600 bg-red-100';
    }

    if (
      estadoNormalizado.includes('PENDIENTE') ||
      estadoNormalizado.includes('REVISION') ||
      estadoNormalizado.includes('REVISIÓN') ||
      estadoNormalizado.includes('INCOMPLETO')
    ) {
      return 'text-yellow-600 bg-yellow-100';
    }

    return 'text-gray-600 bg-gray-100';
  };

  return (
    <MainLayout
      user={user}
      onLogout={onLogout}
      onNavigate={onNavigate}
    >
      <div className="max-w-full mx-auto px-3 py-4">

        {/* =====================================================
            TÍTULO
        ====================================================== */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-1.5 h-8 gradient-red rounded-full shadow-glow-red"></div>

            <h2 className="text-2xl font-bold text-gradient">
              Tracking de Prendas
            </h2>
          </div>

          <p className="text-gray-500 text-sm ml-5">
            Visualice el historial completo de eventos de una prenda
          </p>
        </div>

        {/* =====================================================
            FILTROS DE BÚSQUEDA
        ====================================================== */}
        <div className="bg-gradient-to-br from-white to-gray-50 border border-gray-200 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 p-6 mb-8 relative overflow-hidden">

          <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-red-100 to-transparent rounded-full opacity-20 blur-3xl"></div>

          <div className="relative">

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">

              {/* GRUPO */}
              <div className="group">
                <label className="block text-xs font-semibold text-gray-600 mb-2 uppercase tracking-wide">
                  Grupo
                </label>

                <div className="relative">
                  <input
                    type="text"
                    placeholder="Ingrese número de grupo"
                    value={group}
                    maxLength={6}
                    onChange={(e) => {
                      const sanitized =
                        sanitizeInput(
                          e.target.value
                        );

                      setGroup(sanitized);
                    }}
                    onKeyDown={(e) => {
                      if (
                        e.key === 'Enter' &&
                        !buscando
                      ) {
                        handleBuscar();
                      }
                    }}
                    className="w-full px-4 py-3 bg-white border-2 border-gray-200 rounded-xl text-gray-800 text-sm focus:outline-none focus:border-red-500 focus:ring-4 focus:ring-red-100 transition-all duration-200 group-hover:border-gray-300"
                  />

                  <span className="material-icons absolute right-3 top-3 text-gray-400 text-sm">
                    tag
                  </span>
                </div>
              </div>

              {/* ORDEN */}
              <div className="group">
                <label className="block text-xs font-semibold text-gray-600 mb-2 uppercase tracking-wide">
                  Orden
                </label>

                <div className="relative">
                  <input
                    type="text"
                    placeholder="Ingrese número de orden"
                    value={order}
                    maxLength={4}
                    onChange={(e) => {
                      const sanitized =
                        sanitizeInput(
                          e.target.value
                        );

                      setOrder(sanitized);
                    }}
                    onKeyDown={(e) => {
                      if (
                        e.key === 'Enter' &&
                        !buscando
                      ) {
                        handleBuscar();
                      }
                    }}
                    className="w-full px-4 py-3 bg-white border-2 border-gray-200 rounded-xl text-gray-800 text-sm focus:outline-none focus:border-red-500 focus:ring-4 focus:ring-red-100 transition-all duration-200 group-hover:border-gray-300"
                  />

                  <span className="material-icons absolute right-3 top-3 text-gray-400 text-sm">
                    receipt_long
                  </span>
                </div>
              </div>

              {/* BOTÓN */}
              <div>
                <button
                  onClick={handleBuscar}
                  disabled={buscando}
                  className="w-full px-6 py-3 bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white rounded-xl transition-all duration-200 text-sm font-semibold shadow-lg shadow-red-200 hover:shadow-xl hover:shadow-red-300 disabled:opacity-50 disabled:cursor-not-allowed transform hover:scale-105 active:scale-95 flex items-center justify-center gap-2"
                >
                  <span className="material-icons text-sm">
                    {buscando
                      ? 'hourglass_empty'
                      : 'search'}
                  </span>

                  {buscando
                    ? 'Buscando...'
                    : 'Buscar Prenda'}
                </button>
              </div>

            </div>
          </div>
        </div>

        {/* =====================================================
            MENSAJE DE ERROR
        ====================================================== */}
        {error && !buscando && (
          <div className="bg-red-50 border-2 border-red-200 rounded-2xl shadow-lg overflow-hidden mb-8 animate-fade-in">

            <div className="px-6 py-4">

              <div className="flex items-center gap-3">

                <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="material-icons text-red-600 text-xl">
                    error_outline
                  </span>
                </div>

                <div className="flex-1">
                  <h3 className="font-bold text-red-800 mb-1">
                    Error al buscar
                  </h3>

                  <p className="text-sm text-red-700">
                    {error}
                  </p>
                </div>

                <button
                  onClick={() => setError(null)}
                  className="w-8 h-8 bg-red-100 hover:bg-red-200 rounded-lg flex items-center justify-center transition-colors"
                >
                  <span className="material-icons text-red-600 text-sm">
                    close
                  </span>
                </button>

              </div>

            </div>
          </div>
        )}

        {/* =====================================================
            RESULTADOS
        ====================================================== */}
        {casoEncontrado && (
          <div className="space-y-8 animate-fade-in">

            {/* =================================================
                RESUMEN DE LA PRENDA
            ================================================== */}
            <div className="bg-gradient-to-br from-white via-white to-red-50 border border-gray-200 rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-300 overflow-hidden relative">

              <div className="absolute top-0 right-0 w-96 h-96 bg-gradient-to-br from-red-100 to-transparent rounded-full opacity-30 blur-3xl"></div>

              {/* HEADER */}
              <div className="bg-gradient-to-r from-red-600 to-red-700 px-6 py-3 relative">

                <div className="flex items-center gap-3">

                  <div className="w-8 h-8 bg-white bg-opacity-20 rounded-lg flex items-center justify-center backdrop-blur-sm">
                    <FaHandHoldingUsd className="text-white text-xl" />
                  </div>

                  <div>
                    <h3 className="font-bold text-white">
                      Resumen de la Prenda
                    </h3>

                    <p className="text-red-100 text-xs">
                      Información general del tracking
                    </p>
                  </div>

                </div>
              </div>

              {/* CONTENIDO */}
              <div className="p-6 relative">

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">

                  {/* GRUPO */}
                  <div className="bg-white rounded-xl p-4 border border-gray-100 shadow-sm hover:shadow-md transition-shadow">

                    <div className="flex items-center gap-2 mb-2">
                      <span className="material-icons text-red-500 text-sm">
                        folder
                      </span>

                      <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide">
                        Grupo
                      </label>
                    </div>

                    <p className="text-lg font-bold text-gray-900">
                      {casoEncontrado.grupo}
                    </p>
                  </div>

                  {/* ORDEN */}
                  <div className="bg-white rounded-xl p-4 border border-gray-100 shadow-sm hover:shadow-md transition-shadow">

                    <div className="flex items-center gap-2 mb-2">
                      <span className="material-icons text-red-500 text-sm">
                        receipt
                      </span>

                      <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide">
                        Orden
                      </label>
                    </div>

                    <p className="text-lg font-bold text-gray-900">
                      {casoEncontrado.orden}
                    </p>
                  </div>

                  {/* ESTADO */}
                  <div className="bg-white rounded-xl p-4 border border-gray-100 shadow-sm hover:shadow-md transition-shadow">

                    <div className="flex items-center gap-2 mb-2">
                      <span className="material-icons text-red-500 text-sm">
                        flag
                      </span>

                      <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide">
                        Estado
                      </label>
                    </div>

                    <span
                      className={`inline-flex px-3 py-1.5 rounded-lg text-xs font-bold shadow-sm ${getEstadoColor(
                        casoEncontrado.estado
                      )}`}
                    >
                      {casoEncontrado.estado}
                    </span>
                  </div>

                  {/* TOTAL EVENTOS */}
                  <div className="bg-white rounded-xl p-4 border border-gray-100 shadow-sm hover:shadow-md transition-shadow">

                    <div className="flex items-center gap-2 mb-2">
                      <span className="material-icons text-red-500 text-sm">
                        event_note
                      </span>

                      <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide">
                        Total Eventos
                      </label>
                    </div>

                    <p className="text-lg font-bold text-gray-900">
                      {casoEncontrado.totalEventos}
                    </p>
                  </div>

                </div>
              </div>
            </div>

            {/* =================================================
                TIMELINE DE EVENTOS
            ================================================== */}
            <div className="bg-white border border-gray-200 rounded-2xl shadow-lg overflow-hidden">

              {/* HEADER */}
              <div className="bg-gradient-to-r from-gray-700 to-gray-800 px-6 py-3">

                <div className="flex items-center justify-between">

                  <div className="flex items-center gap-3">

                    <div className="w-8 h-8 bg-white bg-opacity-10 rounded-lg flex items-center justify-center backdrop-blur-sm">
                      <span className="material-icons text-white text-xl">
                        timeline
                      </span>
                    </div>

                    <div>
                      <h3 className="font-bold text-white">
                        Línea de Tiempo
                      </h3>

                      <p className="text-gray-300 text-xs">
                        Historial completo de eventos de la prenda
                      </p>
                    </div>

                  </div>

                  <div className="bg-white bg-opacity-10 px-3 py-1 rounded-lg backdrop-blur-sm">
                    <span className="text-white text-xs font-semibold">
                      {trackingEventos.length} eventos
                    </span>
                  </div>

                </div>
              </div>

              {/* TIMELINE */}
              <div className="p-6">

                <div className="relative">

                  {/* LÍNEA CONTINUA */}
                  <div className="absolute left-5 top-5 bottom-5 w-0.5 bg-black"></div>

                  <div className="space-y-0">

                    {trackingEventos.map(
                      (track, idx) => (
                        <div
                          key={track.id}
                          className="relative flex items-center gap-4"
                        >

                          {/* ICONO */}
                          <div
                            className={`relative flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center border-2 z-10 ${getCategoriaIconoColor(
                              track.categoria
                            )}`}
                          >
                            <span className="material-icons text-white text-lg">
                              {getCategoriaIcono(
                                track.categoria
                              )}
                            </span>
                          </div>

                          {/* CARD DEL EVENTO */}
                          <div className="flex-1 bg-gray-50 hover:bg-gray-100 rounded-xl p-4 border border-gray-200 hover:border-red-300 transition-all duration-200 hover:shadow-md my-2">

                            {/* TÍTULO + ESTADO */}
                            <div className="flex items-start justify-between gap-3 mb-2">

                              <h4 className="font-semibold text-gray-900 text-sm">
                                {track.evento}
                              </h4>

                              <span
                                className={`flex-shrink-0 px-2.5 py-0.5 rounded-md text-xs font-medium ${getCategoriaColor(
                                  track.categoria
                                )}`}
                              >
                                {getCategoriaTexto(
                                  track.categoria
                                )}
                              </span>

                            </div>

                            {/* DETALLE */}
                            <p className="text-xs text-gray-600 mb-2">
                              {track.detalle}
                            </p>

                            {/* INFORMACIÓN */}
                            <div className="flex items-center gap-4 text-xs text-gray-500">

                              {/* FECHA */}
                              <div className="flex items-center gap-1">
                                <span
                                  className="material-icons"
                                  style={{
                                    fontSize: '14px',
                                  }}
                                >
                                  schedule
                                </span>

                                <span>
                                  {track.fecha}
                                </span>
                              </div>

                              {/* USUARIO */}
                              <div className="flex items-center gap-1">
                                <span
                                  className="material-icons"
                                  style={{
                                    fontSize: '14px',
                                  }}
                                >
                                  person
                                </span>

                                <span>
                                  {track.usuario}
                                </span>
                              </div>

                              {/* EVENTO MÁS RECIENTE */}
                              {idx === 0 && (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-100 text-blue-700 rounded-md font-medium">

                                  <span
                                    className="material-icons"
                                    style={{
                                      fontSize: '12px',
                                    }}
                                  >
                                    fiber_manual_record
                                  </span>

                                  Reciente
                                </span>
                              )}

                            </div>
                          </div>
                        </div>
                      )
                    )}

                  </div>
                </div>
              </div>

              {/* FOOTER */}
              <div className="px-6 py-3 bg-gray-50 border-t border-gray-200">

                <div className="text-xs text-gray-500">
                  Total de eventos registrados:{' '}
                  <span className="font-semibold text-gray-700">
                    {trackingEventos.length}
                  </span>
                </div>

              </div>
            </div>
          </div>
        )}

        {/* =====================================================
            ESTADO SIN BÚSQUEDA
        ====================================================== */}
        {!casoEncontrado &&
          !buscando &&
          !error && (
            <div className="bg-gradient-to-br from-white to-gray-50 border-2 border-dashed border-gray-300 rounded-2xl shadow-lg overflow-hidden">

              <div className="px-6 py-16 text-center relative">

                <div className="absolute inset-0 bg-gradient-to-br from-gray-100 to-transparent opacity-30"></div>

                <div className="relative">

                  <div className="inline-flex items-center justify-center w-24 h-24 bg-gradient-to-br from-gray-100 to-gray-200 rounded-full mb-4 shadow-inner">
                    <span className="material-icons text-6xl text-gray-400">
                      history
                    </span>
                  </div>

                  <h3 className="text-xl font-bold text-gray-700 mb-2">
                    No hay resultados para mostrar
                  </h3>

                  <p className="text-gray-500 max-w-md mx-auto">
                    Ingrese un número de grupo y de orden
                    en los campos de búsqueda y presione
                    el botón{' '}
                    <span className="font-semibold">
                      "Buscar Prenda"
                    </span>{' '}
                    para visualizar el tracking de eventos.
                  </p>

                  <div className="mt-6 flex items-center justify-center gap-2 text-sm text-gray-400">

                    <span className="material-icons text-sm">
                      info
                    </span>

                    <span>
                      Complete el grupo y el orden para realizar la búsqueda
                    </span>

                  </div>

                </div>
              </div>
            </div>
          )}

        {/* =====================================================
            LOADER
        ====================================================== */}
        {buscando && (
          <HondaLoader
            message="Buscando información..."
            variant="inline"
            size="medium"
            subtitle="Estamos procesando su solicitud y recuperando el historial de eventos de la prenda"
          />
        )}

      </div>
    </MainLayout>
  );
};

export default TrackingPrendas;